import React, { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FieldType } from 'utils/enum';
import { useParams } from 'react-router-dom';
import { useRLTemplateAndSupplierFieldConfig } from 'queries/estimates';
import { processFields, processInitialValues, processSchema } from 'utils/form';
import { Layout } from 'components/Form';
import Button from 'components/Button';
import { useBidTypeChoices, useRepChoices } from 'queries/company';
import { useJobSupplierChoices, useLightRLTemplateChoices } from 'queries/job';
import ImportManufacturers from '../ImportManufacturers';
import { wastePctChoices } from 'pages/RoofTemplates/utils';
import useModal from 'hooks/useModal';
import RoofTemplateBuilder from 'pages/RoofTemplateBuilder';
import Modal, { Variant } from 'components/Modal';
import Icon from 'components/Icon';
import useAuth from 'hooks/useAuth';

interface IFormValues {
  rep: number;
  bid_type: string;
  template: number;
  waste_pct_choice: string;
  custom_waste_pct: number;
  supplier: number;
  supplier_location?: number;
  srs_job_account?: number;
}

type AppProps = {
  onSubmit: (...args: any) => any;
};

const CreateEstimateFromDrawing: React.FC<AppProps> = ({ onSubmit }) => {
  const { jobId = '' } = useParams();
  const { data: config, isLoading } = useRLTemplateAndSupplierFieldConfig(
    jobId,
    { create_from: 'drawing' }
  );

  const [templateDetail, showTemplateDetail, closeTemplateDetail] = useModal();
  const { userInfo } = useAuth();
  const [supplier, setSupplier] = useState<any>(null);
  const [supplierLocation, setSupplierLocation] = useState<any>(null);
  const [srsJobAccount, setSrsJobAccount] = useState<any>(null);

  const { data: templates } = useLightRLTemplateChoices(jobId, {
    supplier: supplier?.value,
  });

  const schema = yup.object(
    processSchema(
      {
        template: yup.number(),
        rep: yup.number(),
        bid_type: yup.string(),
        waste_pct_choice: yup.string(),
        custom_waste_pct: yup.number(),
        supplier: yup.number().optional(),
        supplier_location: yup.number().nullable().optional(),
        srs_job_account: yup.number().nullable().optional(),
      },
      config,
      ['supplier', 'supplier_location', 'srs_job_account']
    )
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setError,
    setValue,
  } = useForm<IFormValues>({ resolver: yupResolver(schema) });
  const _waste_pct_choice = watch('waste_pct_choice');
  const _template = watch('template');

  useEffect(() => {
    if (config) {
      const defaultValues = processInitialValues(config);
      reset(defaultValues);
    }
  }, [config]);

  const _onSubmit: SubmitHandler<IFormValues> = async (formData) => {
    if (supplier?.integrated) {
      if (!supplierLocation) {
        setError('supplier_location', {
          message: 'Please select a supplier location',
        });
        return;
      }

      if (supplier.supplier_code === 'SRS_SUPPLY' && !srsJobAccount) {
        setError('srs_job_account', {
          message: 'Please select a SRS job account',
        });
        return;
      }
    }
    const payload = {
      ...formData,
      version: 'v2',
      srs_job_account: srsJobAccount
        ? {
            jobAccountNumber: srsJobAccount['value'],
            jobAccountName: srsJobAccount['name'],
          }
        : undefined,
    };
    onSubmit(payload);
  };

  const fields = [
    {
      name: 'template',
      type: FieldType.Select,
      label: 'Select Template',
      placeholder: 'Select Template',
      error: errors.template?.message,
      labelAction: !!_template && (
        <div
          onClick={showTemplateDetail}
          className="relative top-1 text-xs text-primary cursor-pointer"
        >
          View Template Details
        </div>
      ),
      control,
      options: templates ?? [],
      // optionsPromise: () =>
      //   // eslint-disable-next-line react-hooks/rules-of-hooks
      //   useLightRLTemplateChoices(jobId, { supplier: supplier?.id }),
    },
    {
      name: 'waste_pct_choice',
      type: FieldType.Select,
      label:
        'Would you like to use the default waste factors defined in your Roof Template Lineitems, or would you like to override those with a custom waste factor?',
      placeholder: 'Select Waste Factor Choice',
      error: errors.waste_pct_choice?.message,
      control,
      options: [
        {
          label: 'Default',
          value: 'default',
        },
        {
          label: 'Custom',
          value: 'custom',
        },
      ],
    },
  ];

  const fields1 = [
    {
      name: 'custom_waste_pct',
      type: FieldType.Select,
      label: 'Custom Waste %',
      placeholder: 'Select Waste %',
      error: errors.custom_waste_pct?.message,
      control,
      options: wastePctChoices,
    },
  ];

  const fields2 = [
    {
      name: 'rep',
      type: FieldType.AsyncSelect,
      label: 'Select Rep',
      placeholder: 'Select rep',
      error: errors.rep?.message,
      control,
      optionsPromise: useRepChoices,
    },
    {
      name: 'bid_type',
      type: FieldType.AsyncSelect,
      label: 'Select Bid Type',
      placeholder: 'Select Bid Type',
      error: errors.bid_type?.message,
      control,
      optionsPromise: useBidTypeChoices,
      orderByLabel: false,
    },
  ];

  const fields0 = useMemo(() => {
    const _fields = [];
    if (userInfo?.integrations?.srs) {
      _fields.push({
        name: 'supplier',
        type: FieldType.AsyncSelect,
        label: 'Select Supplier',
        placeholder: 'Select Supplier',
        error: errors.supplier?.message,
        orderByLabel: false,
        control,
        // eslint-disable-next-line react-hooks/rules-of-hooks
        optionsPromise: () => useJobSupplierChoices(jobId),
        postSelect: (selected: any) => {
          setSupplier(selected);
          if (selected.integrated && selected.locations?.length > 0) {
            const selectedLocation = selected.locations[0];
            setSupplierLocation(selectedLocation);
            setValue('supplier_location', selectedLocation?.value);
            if (selectedLocation.job_accounts?.length > 0) {
              const selectedJobAccount = selectedLocation.job_accounts[0];
              setSrsJobAccount(selectedJobAccount);
              setValue('srs_job_account', selectedJobAccount?.value);
            }
          } else {
            setSupplierLocation(null);
            setSrsJobAccount(null);
            setValue('supplier_location', undefined);
            setValue('srs_job_account', undefined);
          }
        },
      });
    }

    if (supplier?.integrated && supplier?.locations?.length > 1) {
      _fields.push({
        name: 'supplier_location',
        type: FieldType.Select,
        label: 'Select Supplier Location',
        options: supplier.locations,
        error: errors.supplier_location?.message,
        control,
        postSelect: (selected: any) => {
          setSupplierLocation(selected);
          if (selected.job_accounts?.length > 0) {
            const selectedJobAccount = selected.job_accounts[0];
            setSrsJobAccount(selectedJobAccount);
            setValue('srs_job_account', selectedJobAccount?.value);
          } else {
            setSrsJobAccount(null);
            setValue('srs_job_account', undefined);
          }
        },
      });
    }

    if (supplier?.integrated && supplierLocation?.job_accounts?.length > 1) {
      _fields.push({
        name: 'srs_job_account',
        type: FieldType.Select,
        label: 'Select SRS Job Account',
        options: supplierLocation.job_accounts,
        error: errors.srs_job_account?.message,
        control,
        postSelect: (selected: any) => {
          setSrsJobAccount(selected);
        },
      });
    }
    return _fields;
  }, [
    errors,
    control,
    userInfo,
    supplier,
    supplierLocation,
    srsJobAccount,
    jobId,
  ]);

  // Handle default value assignments in useEffect
  useEffect(() => {
    if (supplier?.integrated && supplier?.locations?.length === 1) {
      setSupplierLocation(supplier.locations[0]);
      setValue('supplier_location', supplier.locations[0]?.value);
    }
  }, [supplier]);

  useEffect(() => {
    if (supplier?.integrated && supplierLocation?.job_accounts?.length === 1) {
      setSrsJobAccount(supplierLocation.job_accounts[0]);
      setValue('srs_job_account', supplierLocation.job_accounts[0]?.value);
    }
  }, [supplierLocation, supplier]);

  if (isLoading) {
    return <div className="center h-40">Fetching form config...</div>;
  }

  if (!templates?.length && !supplier) {
    return <ImportManufacturers />;
  }

  return (
    <div>
      <form onSubmit={handleSubmit(_onSubmit)}>
        {!!fields0.length && (
          <Layout className="mt-4 space-y-4" fields={fields0} />
        )}
        <Layout
          className="mt-4 space-y-4"
          fields={processFields(fields, config)}
        />
        {_waste_pct_choice === 'custom' && (
          <Layout
            className="mt-4 space-y-4"
            fields={processFields(fields1, config)}
          />
        )}
        <Layout
          className="mt-4 space-y-4"
          fields={processFields(fields2, config)}
        />

        <Button label="Create Estimate" className="mt-8" />
      </form>
      {templateDetail && (
        <Modal variant={Variant.FullScreen} open={templateDetail}>
          <div className="relative">
            <Icon
              name="close"
              className="absolute top-3 right-2 z-10"
              onClick={closeTemplateDetail}
              size={28}
            />
            <RoofTemplateBuilder template={_template?.toString()} readOnly />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CreateEstimateFromDrawing;
